import React, { useState, useMemo } from 'react';
import { useBatteryStore } from '../../store/batteryStore';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { Badge } from '../ui/badge';
import { Search, ArrowUpDown, Check, X, AlertTriangle } from 'lucide-react';

export const ResultsTable = () => {
  const { results, selectedResult, selectResult, cells } = useBatteryStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('rank');
  const [sortDirection, setSortDirection] = useState('asc');
  
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  
  const filteredAndSortedResults = useMemo(() => {
    let filtered = results;
    
    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = results.filter(r => {
        const config = `${r.config.series}S${r.config.parallel}P`.toLowerCase();
        const cellName = cells.find(c => c.id === r.config.cell_id)?.name?.toLowerCase() || '';
        return config.includes(term) || cellName.includes(term);
      });
    }
    
    // Sort
    filtered = [...filtered].sort((a, b) => {
      let aVal, bVal;
      
      switch (sortField) {
        case 'rank':
          aVal = a.rank;
          bVal = b.rank;
          break;
        case 'config':
          aVal = a.config.series * 100 + a.config.parallel;
          bVal = b.config.series * 100 + b.config.parallel;
          break;
        case 'voltage':
          aVal = a.specs.nominal_voltage;
          bVal = b.specs.nominal_voltage;
          break;
        case 'capacity':
          aVal = a.specs.capacity_wh;
          bVal = b.specs.capacity_wh;
          break;
        case 'cells':
          aVal = a.specs.total_cells;
          bVal = b.specs.total_cells;
          break;
        case 'valid':
          aVal = a.validation.is_valid ? 1 : 0;
          bVal = b.validation.is_valid ? 1 : 0;
          break;
        default:
          aVal = a.score;
          bVal = b.score;
      }
      
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });
    
    return filtered;
  }, [results, searchTerm, sortField, sortDirection, cells]);
  
  const SortButton = ({ field, children }) => (
    <button
      onClick={() => handleSort(field)}
      className={`flex items-center gap-1 hover:text-cyan-400 transition-colors ${
        sortField === field ? 'text-cyan-400' : 'text-zinc-400'
      }`}
    >
      {children}
      <ArrowUpDown className="w-3 h-3" />
    </button>
  );
  
  if (results.length === 0) {
    return (
      <div className="h-full flex items-center justify-center text-zinc-500" data-testid="results-empty">
        <div className="text-center">
          <div className="font-heading text-sm uppercase tracking-wider mb-1">No Results</div>
          <div className="text-xs">Generate packs to see results</div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="h-full flex flex-col" data-testid="results-table">
      {/* Search bar */}
      <div className="p-3 border-b border-zinc-800">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            placeholder="Search configs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8 bg-zinc-800 border-zinc-700 text-sm"
            data-testid="results-search"
          />
        </div>
      </div>
      
      {/* Table header */}
      <div className="px-3 py-2 bg-zinc-800/50 border-b border-zinc-800 text-xs font-heading uppercase tracking-wider">
        <div className="grid grid-cols-12 gap-2 items-center">
          <div className="col-span-1">
            <SortButton field="rank">#</SortButton>
          </div>
          <div className="col-span-2">
            <SortButton field="config">Config</SortButton>
          </div>
          <div className="col-span-2">
            <SortButton field="voltage">Voltage</SortButton>
          </div>
          <div className="col-span-2">
            <SortButton field="capacity">Capacity</SortButton>
          </div>
          <div className="col-span-2">
            <SortButton field="cells">Cells</SortButton>
          </div>
          <div className="col-span-2">Layout</div>
          <div className="col-span-1">
            <SortButton field="valid">Valid</SortButton>
          </div>
        </div>
      </div>
      
      {/* Results list */}
      <ScrollArea className="flex-1">
        <div className="divide-y divide-zinc-800/50">
          {filteredAndSortedResults.map((result) => (
            <button
              key={result.id}
              onClick={() => selectResult(result)}
              className={`w-full px-3 py-2 text-left transition-colors hover:bg-zinc-800/50 ${
                selectedResult?.id === result.id ? 'bg-blue-600/20 border-l-2 border-blue-500' : ''
              }`}
              data-testid={`result-row-${result.rank}`}
            >
              <div className="grid grid-cols-12 gap-2 items-center text-xs font-mono">
                <div className="col-span-1 text-zinc-500">
                  {result.rank}
                </div>
                <div className="col-span-2">
                  <Badge 
                    variant="outline" 
                    className="text-cyan-400 border-cyan-400/30 bg-cyan-400/10"
                  >
                    {result.config.series}S{result.config.parallel}P
                  </Badge>
                </div>
                <div className="col-span-2 text-zinc-300">
                  {result.specs.nominal_voltage.toFixed(1)}V
                </div>
                <div className="col-span-2 text-zinc-300">
                  {result.specs.capacity_wh.toFixed(0)}Wh
                </div>
                <div className="col-span-2 text-zinc-300">
                  {result.specs.total_cells}
                </div>
                <div className="col-span-2 text-zinc-500 text-[10px]">
                  {result.config.parablock_rows}×{result.config.parablock_cols} L{result.config.layers}
                </div>
                <div className="col-span-1">
                  {result.validation.is_valid ? (
                    <Check className="w-4 h-4 text-green-500" />
                  ) : result.validation.warnings.length > 0 ? (
                    <AlertTriangle className="w-4 h-4 text-yellow-500" />
                  ) : (
                    <X className="w-4 h-4 text-red-500" />
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      </ScrollArea>
      
      {/* Footer */}
      <div className="px-3 py-2 border-t border-zinc-800 text-xs text-zinc-500 font-mono">
        Showing {filteredAndSortedResults.length} of {results.length} results
      </div>
    </div>
  );
};
